import { ICandidate, IJob, ILocation, amplitude } from 'wisestep-api';

const splitByUpperCase = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  return value.split(/(?=[A-Z])/).join(' ');
};

const getJobLocation = (format: string, locations?: ILocation[]): string => {
  if (!locations || locations.length === 0) {
    return '';
  }

  const formattedLocations = locations.map((location) => {
    if (format === 'CITY_STATE') {
      return `${location.city}, ${location.state}`;
    } else if (format === 'CITY_COUNTRY') {
      return `${location.city}, ${location.country}`;
    } else {
      return '';
    }
  });

  return formattedLocations.join(' / ');
};

export const resolveCustomPlaceholders = (
  rtrBody: string,
  candidate: ICandidate,
  job?: IJob
): string => {
  const replacedBody = rtrBody
    .replaceAll(
      '{{candidate.expectedBaseSalaryTimeSpan | splitByUpperCase}}',
      splitByUpperCase(candidate.expectedBaseSalaryTimeSpan)
    )
    .replaceAll(
      "{{candidate.noticePeriod | stringReplace: '_':' '}}",
      candidate.noticePeriod ? candidate.noticePeriod : ''
    )
    .replaceAll(
      "{{candidate.willingToTravel == null ? 'N/A' : (candidate.willingToTravel ? 'Yes' : 'No')}}",
      candidate.willingToTravel ? 'Yes' : 'No'
    )
    .replaceAll(
      '{{candidate.currentSalaryTimeSpan | splitByUpperCase}}',
      splitByUpperCase(candidate.currentSalaryTimeSpan)
    )
    .replaceAll(
      '{{candidate.relocationPreference | splitByUpperCase}}',
      splitByUpperCase(candidate.relocationPreference)
    )
    .replaceAll(
      '[{{job.locations_city_and_state}}]',
      getJobLocation('CITY_STATE', job ? job.locations : [])
    )
    .replaceAll(
      '[{{job.locations_city_and_country}}]',
      getJobLocation('CITY_COUNTRY', job ? job.locations : [])
    )
    .replaceAll('{{job.clientRecruiter}}', candidate.jobClientRecruiterName ?? '')
    .replaceAll(
      '{{job.salaryTimeSpan | splitByUpperCase}}',
      splitByUpperCase(job ? (job.salaryTimeSpan as string) : '')
    )
    .replaceAll('{{SIGNATURE}}', '#SIGNATURE')
    .replaceAll('{{PAGE_BREAK}}', '#PAGE_BREAK')
    .replaceAll(
      '{{candidate.jobPreferencePermanent}}',
      candidate.jobPreferencePermanent ? 'Permanent' : ''
    )
    .replaceAll(
      '{{candidate.jobPreferenceContract}}',
      candidate.jobPreferenceContract ? 'Contract' : ''
    );
  return replacedBody;
};

type StringToString = {
  [name: string]: string[];
};

export const candidatePlaceholderMap: StringToString = {
  '{{candidate.name}}': ['CANDIDATE_NAME', 'Candidate Name'],
  '{{candidate.email}}': ['CANDIDATE_EMAIL', 'Email'],
  '{{candidate.phoneNumber}}': ['CANDIDATE_PHONE', 'Candidate Phone'],
  '{{candidate.source}}': ['CANDIDATE_SOURCE', 'Source'],
  '{{candidate.currentLocation.city}}': ['CANDIDATE_CITY', 'Candiadte City'],
  '{{candidate.currentLocation.state}}': ['CANDIATE_STATE', 'Candidate State'],
  '{{candidate.currentLocation.country}}': ['CANDIDATE_COUNTRY', 'Candidate Country'],
  '{{candidate.totalYearsExperience}}': ['CANDIDATE_TOTAL_EXP', 'Total Experience'],
  '{{candidate.relevantYearsExperience}}': ['CANDIDATE_RELEV_EXP', 'Relevant Experience'],
  '{{candidate.previousJobTitles}}': ['CANDIDATE_PREV_JOB', 'Previous Job Titles'],
  '{{candidate.previousEmployers}}': ['CANDIDATE_PREV_EMPLOYERS', 'Previous Job Employers'],
  '{{candidate.currentJobTitle}}': ['CANDIDATE_CURRENT_JOB', 'Current Job Title'],
  '{{candidate.currentJobType}}': ['CANDIDATE_JOB_TYPE', 'Current Job Type'],
  '{{candidate.expectedSalaryCurrency}}': ['CANDIDATE_EXP_SAL_CURR', 'Expected Salary Currency'],
  '{{candidate.expectedBaseSalary}}': ['CANDIDATE_EXP_BASE_SAL', 'Expected Base Salary'],
  '{{candidate.expectedBaseSalaryTimeSpan | splitByUpperCase}}': [
    'CANDIDATE_EXP_BASE_SAL_TIME_SPAN',
    'Expected Base Salary Time Span',
  ],
  '{{candidate.jobPreferencePermanent}}': ['CANDIDATE_JOB_PREF_PERM', 'Job Preference Permanent'],
  '{{candidate.jobPreferenceContract}}': ['CANDIDATE_JOB_PREF_CONT', 'Job Preference Permanent'],
  '{{candidate.currentSalaryCurrency}}': ['CANDIDATE_CURR_SAL_CURRENCY', 'Current Salary Currency'],
  '{{candidate.currentSalary}}': ['CANDIDATE_CURRENT_SALARY', 'Current Salary'],
  '{{candidate.currentSalaryTimeSpan | splitByUpperCase}}': [
    'CANDIDATE_CURR_SAL_TIME_SPAN',
    'Current Salary Time Span',
  ],
  '{{candidate.currentEmployer}}': ['CANDIDATE_CURR_EMPLOYER', 'Current Employer'],
  '{{candidate.otherCurrentAllowances}}': [
    'CANDIDATE_OTH_CURR_ALLOWANCE',
    'Other Current Allowances',
  ],
  '{{candidate.otherExpectedAllowances}}': [
    'CANDIDATE_OTH_EXP_ALLOWANCE',
    'Other Expected Allowance',
  ],
  '{{candidate.reasonForNewJob}}': ['REASON_FOR_NEW_JOB', 'Reason For New Job'],
  '{{candidate.relocationPreference | splitByUpperCase}}': [
    'RELOCATION_PREF',
    'Relocation Preference',
  ],
  '{{candidate.visaStatus}}': ['VISA_STATUS', 'Visa Status'],
  '{{candidate.visaValidity}}': ['VISA_VALIDITY', 'Visa Validity'],
  '{{candidate.familyWillingToRelocate}}': [
    'FAMILY_WILLING_TO_RELOCATE',
    'Family Willing To Relocate',
  ],
  '{{candidate.interviewingWithOthers}}': ['INTERVIEWING_WITH_OTHERS', 'Interviewing With Others'],
  '{{candidate.messengers}}': ['MESSENGERS', 'Messengers'],
  '{{candidate.skills}}': ['SKILLS', 'Skills'],
  '{{candidate.assessment}}': ['ASSESSMENT', 'Assessment'],
  '{{candidate.workAuthorization}}': ['WORK_AUTHORIZATION', 'Work Authorization'],
  '{{candidate.linkedInURL}}': ['LINKEDIN_URL', 'LinkedIn URL'],
  '{{candidate.education}}': ['EDUCATION', 'Education'],
  '{{candidate.createdOn}}': ['CREATED_ON', 'Created On'],
  '{{candidate.modifiedOn}}': ['MODIFIED_ON', 'Modified On'],
  '{{candidate.createdBy}}': ['CREATED_BY', 'Created By'],
  '{{candidate.recruiterName}}': ['RECRUITER_NAME', 'Recruiter Name'],
  '{{candidate.workspaceName}}': ['WORKSPACE_NAME', 'Workspace Name'],
  "{{candidate.willingToTravel == null ? 'N/A' : (candidate.willingToTravel ? 'Yes' : 'No')}}": [
    'WILLING_TO_TRAVEL',
    'Willing To Travel',
  ],
  "{{candidate.noticePeriod | stringReplace: '_':' '}}": ['NOTICE_PERIOD', 'Notice Period'],
};

export const evaluationAmplitudeEvents = (name: string, clickedOnName: string, workspace) => {
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: 'EVALUATION',
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });
};

export const rtrAmplitudeEvents = (name: string, clickedOnName: string, workspace) => {
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: 'RTR',
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });
};

export const informationReqAmplitudeEvents = (name: string, clickedOnName: string, workspace) => {
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: 'Information Request',
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });
};

export const generateRandomUUId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
